import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Home, TrendingUp, Lightbulb, Calendar, Flame, Moon, Brain, Activity, Coffee, Wine, UtensilsCrossed, Dumbbell, Users, CloudRain, ThermometerSun, Pill, AlertCircle, Tag, Download } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Progress } from "@/components/ui/progress";
import MonthlyHeatmap from "@/components/MonthlyHeatmap";
import ReactMarkdown from 'react-markdown';
import html2pdf from "html2pdf.js";
import { toast as sonnerToast } from "sonner";
import aiInsightsNetwork from "@/assets/ai-insights-network.jpg";
import { sanitizeHTML, sanitizeText } from "@/lib/sanitize";

interface SymptomAverage {
  symptomType: string;
  average: number;
  trend: 'up' | 'down' | 'stable';
}

interface InsightsData {
  recentLogs: any[];
  averages: SymptomAverage[];
  hotFlashCount: number;
  hotFlashAverage: number;
  notes: string[];
  triggers?: any[];
}

const Insights = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [insightsData, setInsightsData] = useState<InsightsData | null>(null);
  const [aiInsights, setAiInsights] = useState("");
  const [userProfile, setUserProfile] = useState<any>(null);
  const [selectedSymptomFilter, setSelectedSymptomFilter] = useState<string | null>(null);

  useEffect(() => {
    loadInsightsData();
  }, []);

  const loadInsightsData = async () => {
    try {
      console.log('Starting to load insights data...');
      const { data: { user } } = await supabase.auth.getUser();
      console.log('User:', user?.id);
      
      if (!user) {
        console.log('No user found, redirecting to auth');
        toast({
          title: "Authentication required",
          description: "Please log in to view insights",
          variant: "destructive"
        });
        navigate("/auth");
        return;
      }

      // Load user profile
      console.log('Loading user profile...');
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (profileError) {
        console.error('Profile error:', profileError);
      }
      console.log('Profile loaded:', profile);
      setUserProfile(profile);

      // Get last 30 days of symptom data
      console.log('Loading symptom logs...');
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const dateString = thirtyDaysAgo.toISOString().split('T')[0];

      const { data: symptoms, error: symptomsError } = await supabase
        .from("symptom_logs")
        .select("*")
        .eq("user_id", user.id)
        .gte("log_date", dateString)
        .order("log_date", { ascending: false });

      if (symptomsError) {
        console.error('Symptoms error:', symptomsError);
        throw symptomsError;
      }
      
      console.log('Symptoms loaded:', symptoms?.length);

      // Get hot flash data
      console.log('Loading hot flashes...');
      const { data: hotFlashes, error: hotFlashesError } = await supabase
        .from("hot_flash_times")
        .select("*")
        .eq("user_id", user.id)
        .gte("log_date", dateString);

      if (hotFlashesError) {
        console.error('Hot flashes error:', hotFlashesError);
        throw hotFlashesError;
      }
      console.log('Hot flashes loaded:', hotFlashes?.length);

      // Get recent notes
      console.log('Loading notes...');
      const { data: notes, error: notesError } = await supabase
        .from("daily_notes")
        .select("notes")
        .eq("user_id", user.id)
        .gte("log_date", dateString)
        .order("log_date", { ascending: false })
        .limit(10);

      if (notesError) {
        console.error('Notes error:', notesError);
        throw notesError;
      }
      console.log('Notes loaded:', notes?.length);

      // Get triggers for correlation analysis
      console.log('Loading triggers...');
      const { data: triggers, error: triggersError } = await supabase
        .from("daily_triggers")
        .select("*")
        .eq("user_id", user.id)
        .gte("log_date", dateString);

      if (triggersError) {
        console.error('Triggers error:', triggersError);
      }
      console.log('Triggers loaded:', triggers?.length);

      // Calculate averages
      const symptomTypes = ['hotflash', 'sleep', 'brainfog', 'mood', 'energy', 'hydration'];
      const averages: SymptomAverage[] = symptomTypes.map(type => {
        const typeSymptoms = symptoms?.filter(s => s.symptom_type === type) || [];
        const avg = typeSymptoms.length > 0
          ? typeSymptoms.reduce((sum, s) => sum + s.intensity, 0) / typeSymptoms.length
          : 0;
        
        return {
          symptomType: type,
          average: Math.round(avg * 10) / 10,
          trend: 'stable' as const
        };
      });

      const data: InsightsData = {
        recentLogs: symptoms || [],
        averages,
        hotFlashCount: hotFlashes?.length || 0,
        hotFlashAverage: hotFlashes?.length ? Math.round((hotFlashes.length / 30) * 10) / 10 : 0,
        notes: notes?.map(n => n.notes).filter(Boolean) || [],
        triggers: triggers || []
      };

      setInsightsData(data);
      console.log('Insights data set successfully');
    } catch (error: any) {
      console.error('Error loading insights:', error);
      toast({
        title: "Error loading data",
        description: error.message || "Failed to load insights data",
        variant: "destructive"
      });
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const analyzeWithAI = async () => {
    if (!insightsData) return;
    
    setAnalyzing(true);
    setAiInsights("");

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-symptoms`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            symptomData: insightsData,
            userProfile
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to analyze symptoms");
      }

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;

        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") {
            streamDone = true;
            break;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              setAiInsights(prev => prev + content);
            }
          } catch (e) {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }

      toast({
        title: "Analysis complete",
        description: "Your personalized insights are ready"
      });
    } catch (error: any) {
      toast({
        title: "Analysis failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDownloadInsightsPDF = async () => {
    if (!aiInsights) {
      sonnerToast.error("No insights to download. Please generate insights first.");
      return;
    }

    sonnerToast.loading("Generating PDF...");

    // Create a temporary element for PDF generation
    const tempDiv = document.createElement('div');
    tempDiv.style.padding = '40px';
    tempDiv.style.fontFamily = 'Arial, sans-serif';
    tempDiv.style.maxWidth = '800px';
    tempDiv.innerHTML = `
      <div style="margin-bottom: 30px; text-align: center;">
        <h1 style="color: #5a9b87; margin-bottom: 10px;">Your Menopause Insights Report</h1>
        <p style="color: #666; font-size: 14px;">Generated on ${new Date().toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        })}</p>
      </div>
      
      <div style="border-left: 4px solid #5a9b87; padding-left: 20px; margin-bottom: 30px;">
        <h2 style="color: #5a9b87; margin-bottom: 15px;">AI-Powered Analysis</h2>
        <div style="line-height: 1.8; color: #333;">
          ${sanitizeHTML(sanitizeText(aiInsights).replace(/\n/g, '<br>'))}
        </div>
      </div>
      
      <div style="margin-top: 40px; padding-top: 20px; border-top: 2px solid #eee; text-align: center;">
        <p style="color: #999; font-size: 12px;">
          This report is for informational purposes only and should be discussed with your healthcare provider.
        </p>
      </div>
    `;

    const opt = {
      margin: 0.5,
      filename: `menopause-insights-${new Date().toISOString().split('T')[0]}.pdf`,
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' as const }
    };

    try {
      await html2pdf().set(opt).from(tempDiv).save();
      sonnerToast.dismiss();
      sonnerToast.success("PDF downloaded successfully!");
    } catch (error) {
      sonnerToast.dismiss();
      sonnerToast.error("Failed to generate PDF. Please try again.");
      console.error("PDF generation error:", error);
    }
  };

  const getSymptomIcon = (type: string) => {
    switch(type) {
      case 'hotflash': return Flame;
      case 'sleep': return Moon;
      case 'brainfog': return Brain;
      case 'energy': return Activity;
      default: return TrendingUp;
    }
  };

  const getSymptomLabel = (type: string) => {
    const labels: Record<string, string> = {
      hotflash: 'Hot Flashes',
      sleep: 'Sleep Quality',
      brainfog: 'Brain Fog',
      mood: 'Mood',
      energy: 'Energy',
      hydration: 'Hydration'
    };
    return labels[type] || type;
  };

  const getTriggerIcon = (triggerType: string) => {
    // Check if it's a custom trigger
    if (triggerType.startsWith('custom_')) {
      return Tag;
    }
    
    const icons: Record<string, any> = {
      stress: Brain,
      caffeine: Coffee,
      alcohol: Wine,
      spicy_food: UtensilsCrossed,
      exercise: Dumbbell,
      social: Users,
      weather: CloudRain,
      heat: ThermometerSun,
      medication: Pill
    };
    return icons[triggerType] || AlertCircle;
  };

  const getTriggerLabel = (triggerType: string): string => {
    // Check if it's a custom trigger
    if (triggerType.startsWith('custom_')) {
      const displayName = triggerType.replace('custom_', '').replace(/_/g, ' ');
      return displayName.charAt(0).toUpperCase() + displayName.slice(1);
    }
    
    const labels: Record<string, string> = {
      stress: "Stress",
      caffeine: "Caffeine",
      alcohol: "Alcohol",
      spicy_food: "Spicy Food",
      exercise: "Exercise",
      social: "Social Event",
      weather: "Weather Change",
      heat: "Hot Environment",
      medication: "Medication"
    };
    return labels[triggerType] || triggerType;
  };

  // Calculate trigger correlations with optional symptom filter
  const getTriggerCorrelations = () => {
    if (!insightsData?.triggers || !insightsData?.recentLogs) return [];

    const triggerCounts: Record<string, { 
      count: number; 
      totalIntensity: number;
      directLinks: number; // Triggers directly linked to episodes
      dayCorrelations: number; // General daily triggers
    }> = {};

    insightsData.triggers.forEach(trigger => {
      // If symptom filter is active, only include triggers associated with symptom logs
      if (selectedSymptomFilter && trigger.symptom_log_id) {
        const relatedLog = insightsData.recentLogs.find(
          log => log.id === trigger.symptom_log_id && log.symptom_type === selectedSymptomFilter
        );
        if (!relatedLog) return; // Skip this trigger if it's not for the selected symptom
      }
      
      // For general daily triggers (no symptom_log_id), filter by matching date with selected symptom
      if (selectedSymptomFilter && !trigger.symptom_log_id) {
        const sameDaySymptoms = insightsData.recentLogs.filter(
          log => log.log_date === trigger.log_date && log.symptom_type === selectedSymptomFilter
        );
        if (sameDaySymptoms.length === 0) return; // Skip if no matching symptom on that day
      }

      if (!triggerCounts[trigger.trigger_type]) {
        triggerCounts[trigger.trigger_type] = { 
          count: 0, 
          totalIntensity: 0,
          directLinks: 0,
          dayCorrelations: 0
        };
      }
      triggerCounts[trigger.trigger_type].count++;

      // CORRECTED LOGIC: Use specific episode intensity if linked, otherwise use day average
      let intensityToAdd = 0;
      
      if (trigger.symptom_log_id) {
        // Trigger is linked to a SPECIFIC episode - use that episode's exact intensity
        const linkedLog = insightsData.recentLogs.find(log => log.id === trigger.symptom_log_id);
        if (linkedLog) {
          intensityToAdd = linkedLog.intensity;
          triggerCounts[trigger.trigger_type].directLinks++;
        }
      } else {
        // General daily trigger - calculate day average for correlation
        const sameDaySymptoms = insightsData.recentLogs.filter(
          log => log.log_date === trigger.log_date && 
                 (!selectedSymptomFilter || log.symptom_type === selectedSymptomFilter)
        );
        intensityToAdd = sameDaySymptoms.length > 0
          ? sameDaySymptoms.reduce((sum, s) => sum + s.intensity, 0) / sameDaySymptoms.length
          : 0;
        triggerCounts[trigger.trigger_type].dayCorrelations++;
      }
      
      triggerCounts[trigger.trigger_type].totalIntensity += intensityToAdd;
    });

    return Object.entries(triggerCounts)
      .map(([trigger, data]) => ({
        trigger,
        count: data.count,
        avgIntensity: data.count > 0 ? Math.round((data.totalIntensity / data.count) * 10) / 10 : 0,
        directLinks: data.directLinks,
        dayCorrelations: data.dayCorrelations
      }))
      .sort((a, b) => b.count - a.count);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Progress value={undefined} className="w-64 mb-4" />
          <p className="text-muted-foreground">Loading your insights...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      {/* Background Image with Fade */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url(${aiInsightsNetwork})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/90 to-background/95" />
      </div>

      {/* Content */}
      <div className="relative z-10">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Link to="/">
              <Button variant="ghost" size="icon">
                <Home className="h-5 w-5" />
              </Button>
            </Link>
          </div>
          <h1 className="text-4xl font-bold mb-2">Your Insights Dashboard</h1>
          <p className="text-muted-foreground text-lg">
            AI-powered analysis of your symptom patterns and personalized recommendations
          </p>
        </div>

        {!insightsData?.recentLogs.length ? (
          <Card className="p-12 text-center">
            <Calendar className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-semibold mb-2">No Data Yet</h2>
            <p className="text-muted-foreground mb-6">
              Start tracking your symptoms to unlock personalized insights
            </p>
            <Link to="/tracker">
              <Button>Go to Tracker</Button>
            </Link>
          </Card>
        ) : (
          <>
            {/* Symptom Averages */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>30-Day Symptom Overview</CardTitle>
                <CardDescription>Average intensity levels for tracked symptoms</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {insightsData.averages.map(avg => {
                    const Icon = getSymptomIcon(avg.symptomType);
                    const percentage = (avg.average / 10) * 100;
                    
                    return (
                      <div key={avg.symptomType} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4 text-primary" />
                            <span className="font-medium">{getSymptomLabel(avg.symptomType)}</span>
                          </div>
                          <span className="text-sm text-muted-foreground">{avg.average}/10</span>
                        </div>
                        <Progress value={percentage} className="h-2" />
                      </div>
                    );
                  })}
                </div>

                {insightsData.hotFlashCount > 0 && (
                  <div className="mt-6 p-4 bg-accent-soft rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Flame className="h-5 w-5 text-accent" />
                      <h3 className="font-semibold">Hot Flash Tracking</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {insightsData.hotFlashCount} hot flashes recorded in the last 30 days
                      (avg {insightsData.hotFlashAverage} per day)
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Monthly Heatmap */}
            <MonthlyHeatmap 
              symptomLogs={insightsData.recentLogs} 
              onSymptomChange={setSelectedSymptomFilter}
            />

            {/* Trigger Correlation Analysis */}
            {insightsData.triggers && insightsData.triggers.length > 0 && (
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-primary" />
                    Trigger Analysis
                    {selectedSymptomFilter && (
                      <span className="text-sm font-normal text-muted-foreground">
                        for {getSymptomLabel(selectedSymptomFilter)}
                      </span>
                    )}
                  </CardTitle>
                  <CardDescription>
                    {selectedSymptomFilter 
                      ? `Triggers associated with ${getSymptomLabel(selectedSymptomFilter).toLowerCase()} based on your logged data`
                      : "Potential factors that may be correlated with your symptoms"}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {getTriggerCorrelations().length > 0 ? (
                      getTriggerCorrelations().map((item) => {
                        const Icon = getTriggerIcon(item.trigger);
                        const hasDirectLinks = item.directLinks > 0;
                        const hasDayCorrelations = item.dayCorrelations > 0;
                        
                        return (
                          <div key={item.trigger} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border/50">
                            <div className="flex items-center gap-3 flex-1">
                              <div className="p-2 rounded-lg bg-primary/10">
                                <Icon className="h-5 w-5 text-primary" />
                              </div>
                              <div className="flex-1">
                                <p className="font-semibold">{getTriggerLabel(item.trigger)}</p>
                                <p className="text-xs text-muted-foreground">
                                  You have indicated this trigger {item.count} {item.count === 1 ? 'time' : 'times'} in the last 30 days
                                </p>
                                <div className="flex gap-2 mt-1">
                                  {hasDirectLinks && (
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800">
                                      {item.directLinks} directly linked
                                    </span>
                                  )}
                                  {hasDayCorrelations && (
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                                      {item.dayCorrelations} by date
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-muted-foreground">Avg Symptom Intensity</p>
                              <p className="text-2xl font-bold text-primary">{item.avgIntensity}/10</p>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-center text-muted-foreground py-4">
                        Start logging triggers in your Tracker to see correlations with your symptoms
                      </p>
                    )}
                  </div>
                  <div className="mt-4 p-3 bg-accent/10 rounded-lg border border-accent/20">
                    <p className="text-xs text-muted-foreground flex items-start gap-2">
                      <Lightbulb className="h-4 w-4 mt-0.5 flex-shrink-0 text-accent" />
                      <span>
                        <strong>How to read this:</strong><br/>
                        • <span className="text-green-700 dark:text-green-400 font-medium">Directly linked</span> = You tagged this trigger on a specific episode (more reliable)<br/>
                        • <span className="text-amber-700 dark:text-amber-400 font-medium">By date</span> = You noted this trigger on a day when you had symptoms (shows possible connection)<br/><br/>
                        <strong>What the numbers mean:</strong> The higher the average intensity, the more likely this trigger affects your symptoms. Keep tracking to spot patterns you can discuss with your doctor.
                      </span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* AI Analysis Section */}
            <Card className="mb-6 bg-gradient-to-br from-primary/5 via-background to-secondary/10 border">
              <CardHeader className="space-y-2 pb-3 pt-3">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <div className="p-1.5 rounded-lg bg-primary/10">
                      <Lightbulb className="h-5 w-5 text-primary" />
                    </div>
                    AI-Powered Insights
                  </CardTitle>
                  {aiInsights && (
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={analyzeWithAI}
                        className="gap-1 text-xs px-2 py-1"
                      >
                        <TrendingUp className="h-3 w-3" />
                        Refresh
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleDownloadInsightsPDF}
                        className="gap-1 text-xs px-2 py-1"
                      >
                        <Download className="h-3 w-3" />
                        Download PDF
                      </Button>
                    </div>
                  )}
                </div>
                <CardDescription className="text-sm">
                  Get personalized recommendations based on your symptom patterns
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0 pb-3">
                {!aiInsights && !analyzing && (
                  <div className="text-center py-3 space-y-2">
                    <div className="mx-auto w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Lightbulb className="h-4 w-4 text-primary" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="text-sm font-semibold">Ready to unlock personalized insights?</h3>
                      <p className="text-xs text-muted-foreground max-w-md mx-auto">
                        Our AI will analyze your symptom patterns and provide tailored recommendations
                      </p>
                    </div>
                    <Button 
                      size="sm" 
                      onClick={analyzeWithAI}
                      className="gap-1 mt-1 text-sm"
                    >
                      <Lightbulb className="h-3 w-3" />
                      Generate Insights
                    </Button>
                  </div>
                )}

                {analyzing && (
                  <div className="py-3 space-y-3">
                    <div className="flex justify-center">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
                        <Brain className="h-4 w-4 text-primary" />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Progress value={undefined} className="w-full h-1.5" />
                      <p className="text-center text-xs font-medium">
                        Analyzing your symptom patterns...
                      </p>
                    </div>
                  </div>
                )}

                {aiInsights && (
                  <div className="space-y-3">
                    <div className="bg-background/60 rounded-lg p-4 border border-border/50">
                      <div className="prose prose-sm max-w-none dark:prose-invert 
                        prose-headings:text-[#5a9b87] prose-headings:font-semibold prose-headings:border-l-4 prose-headings:border-[#5a9b87] prose-headings:pl-3 prose-headings:py-1
                        prose-h2:text-base prose-h2:mt-4 prose-h2:mb-2
                        prose-h3:text-sm prose-h3:mt-3 prose-h3:mb-1.5
                        prose-p:text-foreground prose-p:text-sm prose-p:leading-relaxed prose-p:mb-2
                        prose-strong:text-[#5a9b87] prose-strong:font-semibold
                        prose-ul:space-y-1 prose-ul:my-2
                        prose-li:text-foreground prose-li:text-sm prose-li:relative prose-li:pl-0
                        [&_ul>li]:before:content-['✓'] [&_ul>li]:before:text-[#5a9b87] [&_ul>li]:before:mr-2 [&_ul>li]:before:font-bold
                        [&_p_strong]:text-[#5a9b87]">
                        <ReactMarkdown
                          components={{
                            ul: ({children}) => <ul className="list-none ml-0">{children}</ul>,
                            li: ({children}) => (
                              <li className="flex items-start gap-2">
                                <span className="text-[#5a9b87] font-bold mt-0.5">✓</span>
                                <span className="flex-1">{children}</span>
                              </li>
                            ),
                            p: ({children}) => {
                              const text = String(children);
                              if (text.startsWith('Important:')) {
                                return (
                                  <p className="bg-[#5a9b87]/10 border-l-4 border-[#5a9b87] p-3 rounded-r text-sm my-3">
                                    {children}
                                  </p>
                                );
                              }
                              return <p>{children}</p>;
                            }
                          }}
                        >
                          {aiInsights}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Days Tracked</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-primary">
                    {new Set(insightsData.recentLogs.map(l => l.log_date)).size}
                  </p>
                  <p className="text-sm text-muted-foreground">in the last 30 days</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Symptoms Logged</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-primary">
                    {insightsData.recentLogs.length}
                  </p>
                  <p className="text-sm text-muted-foreground">total entries</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Notes Added</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-primary">
                    {insightsData.notes.length}
                  </p>
                  <p className="text-sm text-muted-foreground">helpful context</p>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
      </div>
    </div>
  );
};

export default Insights;
